export interface OidcSession {
  access_token: string;
  /** Epoch seconds. */
  expires_at?: number;
}

export interface AccessTokenSources {
  getOidcUser: () => OidcSession | null | undefined;
  getStoredToken: () => Promise<string | null>;
  renew: () => Promise<OidcSession | null>;
  /** Epoch seconds. */
  now?: () => number;
}

// Renewing a little early covers a request still in flight when the token lapses, and a
// browser clock slightly behind the gateway's. It stays under oidc-client-ts's 60 s expiring
// notification, so an awake tab's automatic renewal is not raced by a second one.
const RENEWAL_MARGIN_SECONDS = 30;

/**
 * The access token for the next API call. An SSO session is renewed when it has expired or is
 * about to: its renewal timer does not run while the tab sleeps, and the gateway rejects a
 * call made with an expired token, or with none, before it reaches the BFF. Without a valid SSO
 * session the stored token comes first, since a credentials login refreshes its own. Concurrent
 * callers share one renewal.
 */
export const createAccessTokenProvider = ({
  getOidcUser,
  getStoredToken,
  renew,
  now = () => Date.now() / 1000,
}: AccessTokenSources): (() => Promise<string | null>) => {
  let renewed: OidcSession | null = null;
  let renewal: Promise<OidcSession | null> | null = null;

  const secondsLeft = (session: OidcSession) =>
    session.expires_at === undefined ? Infinity : session.expires_at - now();

  // The auth context only learns of a renewal on its next render, so until then the session
  // this provider renewed is the newer one.
  const currentSession = (): OidcSession | null => {
    const user = getOidcUser();
    if (!user) return null;
    return renewed && secondsLeft(renewed) > secondsLeft(user) ? renewed : user;
  };

  const isSsoToken = (token: string) => token === getOidcUser()?.access_token || token === renewed?.access_token;

  return async () => {
    const session = currentSession();
    if (!session) return getStoredToken();
    if (secondsLeft(session) >= RENEWAL_MARGIN_SECONDS) return session.access_token;

    // An SSO session that could not be renewed stays in the store. A credentials login made after
    // it answers here with its own token, and renewing the old session instead would send the
    // calls as the previous SSO user.
    const stored = await getStoredToken();
    if (stored && !isSsoToken(stored)) return stored;

    renewal ??= renew()
      .catch(() => null)
      .finally(() => {
        renewal = null;
      });

    const result = await renewal;
    if (result) {
      renewed = result;
      return result.access_token;
    }

    return secondsLeft(session) > 0 ? session.access_token : null;
  };
};
